import React, { useEffect, useState } from 'react';
import { Modal, Loader } from 'semantic-ui-react';
import styled from '@emotion/styled';
import axios from 'axios';
import colors from 'css/colors';
import Button from 'components/ui/Button';
import { IFile } from './types';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  padding: 8px;
`;

const Thumb = styled.div<{ selected: boolean }>`
  border: 3px solid ${p => (p.selected ? colors.red : 'transparent')};
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  &:hover {
    border-color: ${p => (p.selected ? colors.red : colors.grey)};
  }
  img {
    border: 1px solid ${colors.greyBorder};
    width: 100%;
  }
`;

export interface IPageSource {
  // Either a document already stored on the order, or a file that was just added to the upload.
  documentId?: number;
  file?: string;
  name: string;
  // Index of the upload-list file these pages replace; undefined when adding a new file.
  replaceIndex?: number;
}

export interface IPickedPages {
  file: IFile;
  replaceIndex?: number;
}

interface IProps {
  sources: IPageSource[];
  onCancel: () => void;
  onConfirm: (picked: IPickedPages[]) => void;
}

// [1,2,3,5,8,9] -> "1-3, 5, 8-9"
const describePages = (pages: number[]) => {
  const sorted = [...pages].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = sorted[0];
  let prev = start;
  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    if (current !== prev + 1) {
      parts.push(start === prev ? `${start}` : `${start}-${prev}`);
      start = current;
    }
    prev = current;
  }
  return parts.join(', ');
};

const errorMessage = (e: any, fallback: string) => {
  const message = e.response?.data;
  return typeof message === 'string' && message ? message : fallback;
};

const PagePicker = ({ sources, onCancel, onConfirm }: IProps) => {
  const [loadingSources, setLoadingSources] = useState<boolean[]>(sources.map(() => true));
  const [error, setError] = useState<string>(null);
  const [thumbnails, setThumbnails] = useState<string[][]>(sources.map(() => []));
  const [selected, setSelected] = useState<number[][]>(sources.map(() => []));
  const [lastClicked, setLastClicked] = useState<number[]>(sources.map(() => null));
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    // Each document shows its pages as soon as it has loaded, rather than waiting for all of them.
    sources.forEach((source, si) => {
      (source.documentId != null
        ? axios.get(`/api/documents/PdfPageThumbnails/${source.documentId}`)
        : axios.post('/api/documents/PdfPageThumbnails', { file: source.file })
      )
        .then(({ data }) => {
          if (!Array.isArray(data?.thumbnails)) {
            setError(`The server did not return any page previews for ${source.name}.`);
            return;
          }
          setThumbnails(prev => prev.map((t, i) => (i === si ? (data.thumbnails as string[]) : t)));
        })
        .catch(e => setError(errorMessage(e, `Could not load ${source.name} (server error).`)))
        .finally(() => setLoadingSources(prev => prev.map((l, i) => (i === si ? false : l))));
    });
  }, []);

  const setAt = <T,>(arr: T[], index: number, value: T) => arr.map((v, i) => (i === index ? value : v));

  const onPageClick = (si: number, page: number, shiftKey: boolean) => {
    const current = selected[si];
    let next: number[];
    if (shiftKey && lastClicked[si] != null) {
      const [from, to] = [Math.min(lastClicked[si], page), Math.max(lastClicked[si], page)];
      const range = Array.from({ length: to - from + 1 }, (_, i) => from + i);
      next = Array.from(new Set([...current, ...range]));
    } else {
      next = current.includes(page) ? current.filter(p => p !== page) : [...current, page];
    }
    setSelected(setAt(selected, si, next));
    setLastClicked(setAt(lastClicked, si, page));
  };

  const totalSelected = selected.reduce((sum, s) => sum + s.length, 0);

  const onUseClick = async () => {
    setExtracting(true);
    setError(null);
    try {
      const chosen = sources
        .map((source, si) => ({ source, pages: selected[si] }))
        .filter(c => c.pages.length > 0);
      if (chosen.length === 1) {
        const { source, pages } = chosen[0];
        const { data } = await axios.post('/api/documents/ExtractPdfPages', {
          documentId: source.documentId,
          file: source.documentId != null ? undefined : source.file,
          pages,
        });
        const baseName = source.name.replace(/\.pdf$/i, '');
        onConfirm([
          {
            file: {
              file: data.file,
              customFileName: null,
              actualFileName: `${baseName} pages ${describePages(pages)}.pdf`,
            },
            replaceIndex: source.replaceIndex,
          },
        ]);
      } else {
        // Several documents: merge the chosen pages into one PDF.
        const { data } = await axios.post('/api/documents/ExtractPdfPagesFromMany', {
          sources: chosen.map(({ source, pages }) => ({
            documentId: source.documentId,
            file: source.documentId != null ? undefined : source.file,
            pages,
          })),
        });
        onConfirm([
          {
            file: { file: data.file, customFileName: null, actualFileName: 'Combined pages.pdf' },
          },
        ]);
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not extract those pages.'));
      setExtracting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      closeIcon
      style={{ backgroundColor: '#fff', borderRadius: 5, padding: 32, width: '85%', maxWidth: 1000 }}
    >
      <div style={{ fontSize: 20, fontWeight: 'bold' }}>
        Select pages from {sources.length === 1 ? sources[0].name : `${sources.length} documents`}
      </div>
      <p style={{ color: colors.greyDark, marginTop: 8 }}>
        Click pages to select them (shift-click for a range). Pages from all documents are combined into one
        file.
      </p>
      {error && <div style={{ color: colors.red, marginBottom: 8 }}>{error}</div>}
      <div style={{ maxHeight: 560, overflowY: 'auto' }}>
        {sources.map((source, si) =>
          loadingSources[si] ? (
            <div key={si} style={{ textAlign: 'center', padding: '48px 0' }}>
              <Loader active inline="centered" size="large" />
              <div style={{ color: colors.greyDark, marginTop: 16 }}>
                Loading pages{sources.length > 1 ? ` from ${source.name}` : ''}... large files can take a
                moment.
              </div>
            </div>
          ) : thumbnails[si].length > 0 ? (
            <div key={si} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                {sources.length > 1 && (
                  <div style={{ fontWeight: 'bold', marginRight: 8 }}>{source.name}</div>
                )}
                <Button
                  secondary
                  onClick={() =>
                    setSelected(
                      setAt(
                        selected,
                        si,
                        thumbnails[si].map((_, i) => i + 1)
                      )
                    )
                  }
                >
                  Select all
                </Button>
                <Button
                  secondary
                  onClick={() => setSelected(setAt(selected, si, []))}
                  disabled={selected[si].length === 0}
                >
                  Clear
                </Button>
              </div>
              <Grid>
                {thumbnails[si].map((src, i) => {
                  const page = i + 1;
                  return (
                    <Thumb
                      key={page}
                      selected={selected[si].includes(page)}
                      onClick={e => onPageClick(si, page, e.shiftKey)}
                    >
                      <img src={src} alt={`${source.name} page ${page}`} draggable={false} />
                      <div style={{ fontSize: 13, marginTop: 4 }}>{page}</div>
                    </Thumb>
                  );
                })}
              </Grid>
            </div>
          ) : null
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
        <Button secondary onClick={onCancel} disabled={extracting}>
          Cancel
        </Button>
        <Button onClick={onUseClick} disabled={totalSelected === 0} loadingStatus={extracting}>
          {totalSelected === 0
            ? 'Select pages'
            : sources.length === 1
            ? `Use page${totalSelected > 1 ? 's' : ''} ${describePages(selected[0])}`
            : `Use ${totalSelected} page${totalSelected > 1 ? 's' : ''}`}
        </Button>
      </div>
    </Modal>
  );
};

export default PagePicker;
